// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck nx global declarations issue (only on build/serve)

import React from 'react'

import type { ApolloQueryResult } from '@edge/spark/io-apollo'
import { get } from 'lodash'
import {
    TableInstance,
    TableOptions,
    usePagination,
    UsePaginationInstanceProps,
    useTable,
    useRowSelect,
    useSortBy,
    useFlexLayout,
} from 'react-table'
import { useHistory, useLocation } from 'react-router-dom'

import { filterMaybe, useSearchParams } from '@edge/spark/utils'
import tableHooks from './tableHooks'
import { sortingOrderConfig, sortingTypeConfig } from '../utils/searchSortConfig'
import type { RowsPerPageOptions } from '../types'

const INITIAL_PAGE_SIZE = 10
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
export const PAGE_SIZE_LOCAL_STORAGE_KEY = 'TABLE_PAGE_SIZE'

// only allow default PAGE_SIZE_OPTIONS to be retrieved from storage
export const getUserSelectedTablePageSize = (): RowsPerPageOptions => {
    const userPageSize = Number(localStorage.getItem(PAGE_SIZE_LOCAL_STORAGE_KEY))
    const foundSizeOption = PAGE_SIZE_OPTIONS.find((option) => option === userPageSize)

    return (foundSizeOption || INITIAL_PAGE_SIZE) as RowsPerPageOptions
}

// TODO: Improve Typings
// https://github.com/tannerlinsley/react-table/issues/1591
// "Working on it right now. ETA 2021" or next major update?
// UsePaginationInstanceProps and UsePaginationState not properly in @types
interface ExtendedUseTableInstance<D extends object = Record<string, unknown>>
    extends TableInstance<D>,
        UsePaginationInstanceProps<D> {
    count: number
    title: string
    loading: boolean
    res: any
}

export type ApolloLazyQueryHook<T> = (arg?: any) => [(arg: any) => void, Partial<ApolloQueryResult<T>>]

export interface QueryOptions {
    variables: Record<string, unknown>
}

export interface Props<D extends Record<string, unknown>> {
    columns: TableOptions<D>['columns']
    countPath?: string
    titlePath?: string
    lazyQueryHook: ApolloLazyQueryHook<any>
    listPath?: string
    queryOptions?: QueryOptions
    paginated?: boolean
    useRowSelect?: boolean
    debounceValue?: string
    flex?: boolean
    sortString?: string
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export const usePaginatedApolloTable = <D extends Record<string, unknown>>({
    listPath = '',
    countPath = '',
    titlePath = '',
    lazyQueryHook: useLazyQuery,
    queryOptions,
    columns,
    paginated = true,
    debounceValue,
    flex,
    sortString,
}: Props<D>): ExtendedUseTableInstance<D> => {
    const { filter, page } = useSearchParams({
        filter: '',
        page: '1',
    })

    const history = useHistory()
    const location = useLocation()

    const [pageCount, setPageCount] = React.useState(0)

    const searchStringKey = queryOptions?.variables ? Object.keys(queryOptions.variables)[0] : 'searchString'
    const searchString = queryOptions?.variables?.[searchStringKey]
    const filteredQueryOptions =
        filter && typeof searchString === 'string'
            ? {
                  variables: {
                      [searchStringKey]: `${searchString}${debounceValue ? ` ${debounceValue}` : ''}`,
                      limit: getUserSelectedTablePageSize(),
                      offset: 0,
                  },
              }
            : queryOptions

    const filteredVariables = filteredQueryOptions?.variables
    const modQueryOptions = sortString
        ? {
              variables: {
                  ...filteredVariables,
                  sortType: sortingTypeConfig(sortString),
                  sortOrder: sortingOrderConfig(sortString),
              },
          }
        : filteredQueryOptions

    const [fetchData, { data, loading }] = useLazyQuery({
        fetchPolicy: 'cache-and-network',
        ...modQueryOptions,
    })

    const memoizedItems = React.useMemo(() => {
        const maybeItemsArray = get(data, listPath)
        const items = Array.isArray(maybeItemsArray) ? maybeItemsArray : []

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return items.filter(filterMaybe)
    }, [data, listPath])

    const count = get(data, countPath, memoizedItems.length)
    const title = get(data, titlePath, 'Search Results')

    const userSelectedPageSize = getUserSelectedTablePageSize()

    const tableInstance = useTable<D>(
        {
            columns,
            data: memoizedItems,
            manualPagination: true,
            pageCount,
            initialState: {
                pageIndex: page ? +page - 1 : 0,
                pageSize: userSelectedPageSize,
            },
        },
        tableHooks,
        useSortBy,
        usePagination,
        useRowSelect,
        flex ? useFlexLayout : () => undefined,
    )

    const {
        gotoPage,
        state: { pageSize },
    } = tableInstance

    React.useEffect(() => {
        const limit = pageSize
        const offset = (+page - 1) * pageSize
        localStorage.setItem(PAGE_SIZE_LOCAL_STORAGE_KEY, JSON.stringify(pageSize))

        const options = paginated
            ? {
                  variables: { limit, offset },
              }
            : undefined

        fetchData(options)
    }, [fetchData, paginated, page, pageSize])

    React.useEffect(() => {
        const query = new URLSearchParams(location.search)
        const doesFilterMatchDebounce = debounceValue === decodeURIComponent(filter)
        if (debounceValue && !doesFilterMatchDebounce) {
            query.set('filter', `${encodeURIComponent(debounceValue)}`)
            query.delete('page')
        } else if (debounceValue === '') {
            query.delete('filter')
        }
        history.replace({ search: query.toString() })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounceValue])

    React.useEffect(() => {
        setPageCount(Math.ceil(count / pageSize))
    }, [setPageCount, count, pageSize])

    // TablePaginationProps
    const onChangePage = (
        _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        nextPageIndex: number,
    ): void => {
        gotoPage(nextPageIndex)
        const nextPageNumber = nextPageIndex + 1
        const query = new URLSearchParams(location.search)
        if (nextPageNumber > 1) {
            query.set('page', `${nextPageNumber}`)
        } else {
            query.delete('page')
        }
        history.replace({ search: query.toString() })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useLayoutEffect(() => onChangePage(null, 0), [pageSize])

    return {
        ...tableInstance,
        count,
        title,
        loading: !!loading,
        res: data,
        onChangePage,
    }
}
